import React, { useCallback, useEffect, useState } from 'react';
import { api, todayStr } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState, Spinner, PriorityChip } from '@/components/common';
import { toast } from 'sonner';
import { ListTodo, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function Tasks() {
  const [tasks, setTasks] = useState(null);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('open');
  const { stagesData } = useApp();
  const [form, setForm] = useState({ title: '', category: 'Research', priority: '2', product_id: 'none', due_date: todayStr() });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [t, p] = await Promise.all([api.get('/tasks'), api.get('/products')]);
    setTasks(t.data);
    setProducts(p.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.title.trim()) { toast.error('Task title required'); return; }
    setBusy(true);
    try {
      await api.post('/tasks', {
        title: form.title.trim(), category: form.category, priority: Number(form.priority),
        product_id: form.product_id === 'none' ? null : form.product_id, due_date: form.due_date || null,
      });
      setForm((f) => ({ ...f, title: '' }));
      toast.success('Task added');
      load();
    } finally { setBusy(false); }
  };

  const toggle = async (t) => {
    await api.put(`/tasks/${t.id}`, { done: !t.done });
    load();
  };

  const del = async (id) => {
    await api.delete(`/tasks/${id}`);
    load();
  };

  const filtered = (tasks || []).filter((t) => (filter === 'open' ? !t.done : filter === 'done' ? t.done : true));
  const productName = (id) => products.find((p) => p.id === id)?.name;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Tasks</h1>
        <div className="page-accent-bar" />
        <p className="mt-2 text-sm text-muted-foreground">Store setup, creative, CS, fulfillment — everything that moves a test forward.</p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <Input data-testid="task-form-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="e.g. Write 3 hooks for the pain-relief angle" />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="task-form-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(stagesData.task_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
              <SelectTrigger data-testid="task-form-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">P1 — Critical</SelectItem>
                <SelectItem value="2">P2 — Normal</SelectItem>
                <SelectItem value="3">P3 — Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.product_id} onValueChange={(v) => setForm((f) => ({ ...f, product_id: v }))}>
              <SelectTrigger data-testid="task-form-product-select"><SelectValue placeholder="Product" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No product</SelectItem>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="task-form-due-input" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} />
          </div>
          <Button data-testid="task-form-submit-button" onClick={add} disabled={busy} className="accent-btn w-full gap-2 sm:w-auto">
            {busy ? <Spinner /> : <Plus className="h-4 w-4" />} Add Task
          </Button>
        </CardContent>
      </Card>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger data-testid="tasks-filter-open" value="open">Open</TabsTrigger>
          <TabsTrigger data-testid="tasks-filter-done" value="done">Done</TabsTrigger>
          <TabsTrigger data-testid="tasks-filter-all" value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {tasks === null ? (
        <div className="flex justify-center py-10"><Spinner className="h-6 w-6" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState testId="tasks-empty" icon={ListTodo} title={filter === 'done' ? 'Nothing completed yet' : 'No open tasks'} subtitle="Add a task above, or qualify a product to auto-generate its test checklist." />
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {filtered.map((t) => (
              <div key={t.id} data-testid="task-row" className="flex items-center gap-3 px-4 py-3">
                <Checkbox data-testid="task-toggle-checkbox" checked={t.done} onCheckedChange={() => toggle(t)} />
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm', t.done && 'text-muted-foreground line-through')}>{t.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {t.category}{t.due_date ? ` · due ${t.due_date}` : ''}{t.product_id && productName(t.product_id) ? ` · ${productName(t.product_id)}` : ''}
                  </p>
                </div>
                {!t.done && <PriorityChip priority={t.priority || 3} />}
                <Button data-testid="task-delete-button" variant="ghost" size="icon" aria-label="Delete task" onClick={() => del(t.id)}>
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
